
// Configure React Router v7 future flags globally to avoid warnings
if (typeof window !== 'undefined') {
  window.__reactRouterFutureFlags = {
    v7_startTransition: true,
    v7_relativeSplatPath: true
  };
}

import React, { useCallback, useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate } from 'react-router-dom';
import HomeView from './views/HomeView';
import CharacterSelectView from './views/CharacterSelectView';
import StoryView from './views/StoryView';
import ProfileView from './views/ProfileView';
import AuthView from './views/AuthView';
import CreateChildProfileView from './views/CreateChildProfileView';
import EmailVerificationView from './views/EmailVerification/EmailVerificationView';
import { useStory } from './contexts/StoryContext';
import { useAuth } from './contexts/AuthContext';
import AppProviders from './contexts/AppProviders';
import { LoadingIndicator } from './components/ui';
import { ProtectedRoute, ChildProfileForm } from './components/auth';
import { Header } from './components/layout';
import './App.css';

// Export AppRoutes for testing purposes
export function AppRoutes() {
  const { story, isLoading, isCreatingStory, error, resetStory } = useStory();
  const navigate = useNavigate();
  
  // Audio state for background music
  const [audioPlaying, setAudioPlaying] = useState(false);
  const [audioElement, setAudioElement] = useState(null);
  
  // Available audio tracks
  const audioTracks = [
    '/weaversworld/assets/audio/serene-horizon-260026.mp3',
    '/weaversworld/assets/audio/midnight-forest-184304.mp3',
    '/weaversworld/assets/audio/winter-magic-275005.mp3'
  ];
  
  // Initialize background audio
  useEffect(() => {
    // Select random background track
    const randomTrackIndex = Math.floor(Math.random() * audioTracks.length);
    const selectedTrack = audioTracks[randomTrackIndex];
    
    const audio = new Audio(selectedTrack);
    audio.loop = true;
    audio.volume = 0.3;
    setAudioElement(audio);
    
    // Cleanup function
    return () => {
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, []);
  
  // Toggle background audio
  const toggleAudio = () => {
    if (!audioElement) return;
    
    if (audioPlaying) {
      audioElement.pause();
    } else {
      audioElement.play().catch(err => {
        console.error('Error playing audio:', err);
      });
    }
    
    setAudioPlaying(!audioPlaying);
  };

  // Handle story selection from HomeView
  const handleStorySelect = useCallback((storyId) => {
    navigate(`/weaversworld/story/${storyId}`);
  }, [navigate]);

  // Get auth state
  const { isAuthenticated, activeChildProfile, loading: authLoading } = useAuth();

  // Handle creating a new story from HomeView
  const handleCreateNewStory = useCallback(() => {
    // Reset story state before navigating
    resetStory();
    
    // If authenticated but no child profile exists, create one first
    if (isAuthenticated && !activeChildProfile) {
      // Navigate to child profile creation with state indicating we're coming from first-run
      navigate('/child-profiles/create', { state: { fromFirstRun: true } });
    } else {
      // If not authenticated or child profile exists, go directly to story creation
      navigate('/weaversworld/create');
    }
  }, [navigate, resetStory, isAuthenticated, activeChildProfile]);

  // No full-page loading spinners allowed!
  // Logging removed for production

  return (
    <div className="app" data-testid="app">
      {/* Application Header */}
      <Header 
        audioPlaying={audioPlaying}
        onAudioToggle={toggleAudio}
      />
      
      {/* Error display */}
      {error && <div className="error-message">{error}</div>}
      
      <Routes>
        {/* Home view with story list */}
        <Route path="/weaversworld" element={
          <HomeView 
            onStorySelect={handleStorySelect}
            onCreateNewStory={handleCreateNewStory}
          />
        } />
        
        {/* Character selection for new story - allows anonymous users or requires child profile for authenticated users */}
        <Route path="/weaversworld/create" element={
          isCreatingStory ? (
            <Navigate to={`/weaversworld/story/${story.id}`} replace />
          ) : (
            <ProtectedRoute 
              requireChildProfile={isAuthenticated} 
              allowAnonymous={true}
            >
              <CharacterSelectView />
            </ProtectedRoute>
          )
        } />
        
        {/* Unified story view for both creation and playback - allows anonymous users or requires child profile for authenticated users */}
        <Route path="/weaversworld/story/:story_id" element={
          <ProtectedRoute 
            requireChildProfile={isAuthenticated} 
            allowAnonymous={true}
          >
            <StoryView />
          </ProtectedRoute>
        } />
        
        {/* Authentication routes */}
        <Route path="/accounts/:action" element={<AuthView />} />
        
        {/* Email verification route */}
        <Route path="/accounts/email/verification/:key" element={<EmailVerificationView />} />
        
        {/* Protected routes */}
        <Route path="/profile" element={
          <ProtectedRoute>
            <ProfileView />
          </ProtectedRoute>
        } />
        
        {/* Child profile routes */}
        <Route path="/child-profiles/create" element={
          <ProtectedRoute>
            <CreateChildProfileView />
          </ProtectedRoute>
        } />
        
        <Route path="/child-profiles/:profile_id/stories" element={
          <ProtectedRoute requireChildProfile={true}>
            <div className="content-container">
              {/* We'll add the actual component here later */}
              <h1>Child Profile Stories</h1>
            </div>
          </ProtectedRoute>
        } />
        
        {/* Default redirect */}
        <Route path="*" element={<Navigate to="/weaversworld" replace />} />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <Router future={{
      v7_startTransition: true,
      v7_relativeSplatPath: true
    }}>
      <AppProviders>
        <AppRoutes />
      </AppProviders>
    </Router>
  );
}

export default App;
